import { gql as contentfulGQL } from '@apollo/client'
import { ContentfulClientOptions, getContentfulClient } from '@/services/ApolloClient'
import {
  FeaturedProject,
  GetFeaturedProjectsByProjectSlugAndPageQuery,
  GetFeaturedProjectsQuery,
} from '@/types/codegen-contentful'
import { getFirstCollectionItemAs } from '@/utils/contentful/collection-helpers'

export const GET_FEATURED_PROJECT_BY_ID_QUERY = contentfulGQL/* GraphQL */ `
  query getFeaturedProjectById($id: String!, $locale: String, $preview: Boolean) {
    featuredProjectCollection(locale: $locale, preview: $preview, limit: 1, where: { sys: { id: $id } }) {
      items {
        backgroundImageExperiment
        campaign
        contentCollection {
          items {
            cloudinaryImagePath
            contentType
            group
            guid
            logoOverlayClassName
            logoOverlayCloudinaryPath
            project
            source
            title
          }
        }
        ctaPosition
        guildMemberHref
        href
        logoHeight
        logoUrl
        logoWidth
        mobileBackgroundUrl
        tabletBackgroundUrl
        desktopBackgroundUrl
        ultrawideBackgroundUrl
        pillBackgroundColor
        pillLabel
        projectSlug
        showGuildCta
        showTicketsCta
        showWatchCta
        showLearnMoreCta
        sys {
          id
        }
        status
        subHeading
        textColor
        sneakPeekUrl
        projectTheme {
          sneakPeekDuration
        }
      }
    }
  }
`

export const getFeaturedProjectsQuery = contentfulGQL/* GraphQL */ `
  query getFeaturedProjects($locale: String, $preview: Boolean) {
    featuredProjectCollection(
      limit: 3
      locale: $locale
      order: sys_publishedAt_DESC
      preview: $preview
      where: { page: "watch-landing-page" }
    ) {
      items {
        sys {
          id
        }
      }
    }
  }
`
export const GET_FEATURED_PROJECT_BY_PROJECT_SLUG_AND_PAGE_QUERY = contentfulGQL/* GraphQL */ `
  query getFeaturedProjectsByProjectSlugAndPage(
    $projectSlug: String
    $page: String
    $locale: String
    $preview: Boolean
  ) {
    featuredProjectCollection(
      limit: 1
      locale: $locale
      order: sys_publishedAt_DESC
      preview: $preview
      where: { page: $page, projectSlug: $projectSlug }
    ) {
      items {
        backgroundImageExperiment
        campaign
        contentCollection {
          items {
            cloudinaryImagePath
            contentType
            group
            guid
            logoOverlayClassName
            logoOverlayCloudinaryPath
            project
            source
            title
          }
        }
        ctaPosition
        desktopBackgroundUrl
        guildMemberHref
        heading
        guildHeading
        href
        logoHeight
        logoUrl
        logoWidth
        mobileBackgroundUrl
        pillBackgroundColor
        pillLabel
        projectSlug
        showGuildCta
        showTicketsCta
        showWatchCta
        showLearnMoreCta
        sys {
          id
        }
        status
        subHeading
        textColor
        ultrawideBackgroundUrl
      }
    }
  }
`

export async function getFeaturedProjects(opts: ContentfulClientOptions): Promise<FeaturedProject[]> {
  const { data } = await getContentfulClient(opts).query<GetFeaturedProjectsQuery>({
    query: getFeaturedProjectsQuery,
    variables: { preview: opts?.preview },
    errorPolicy: 'all',
  })

  return (data?.featuredProjectCollection?.items ?? []).filter((item): item is FeaturedProject => item !== null)
}

export async function getFeaturedProjectByProjectSlugAndPage({
  projectSlug,
  page,
  ...opts
}: { projectSlug: string; page: string } & ContentfulClientOptions): Promise<FeaturedProject | null> {
  const { data } = await getContentfulClient(opts).query<GetFeaturedProjectsByProjectSlugAndPageQuery>({
    query: GET_FEATURED_PROJECT_BY_PROJECT_SLUG_AND_PAGE_QUERY,
    variables: { preview: opts?.preview, projectSlug, page },
    errorPolicy: 'all',
  })

  return getFirstCollectionItemAs<FeaturedProject>(data?.featuredProjectCollection?.items)
}
